import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { authAPI } from '../api/client';

function EyeIcon() {
  return (
    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
    </svg>
  );
}

function EyeOffIcon() {
  return (
    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
    </svg>
  );
}

function Spinner() {
  return (
    <svg className="h-5 w-5 animate-spin" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
    </svg>
  );
}

const brandFeatures = [
  'Live cash flow and revenue visibility',
  'Invoices, estimates, and collections in one workspace',
  'Expenses, tax reporting, and project tracking built in',
];

const authInputClass =
  'w-full rounded-[20px] border border-black/8 bg-white px-4 py-3.5 text-sm text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-emerald-600 focus:ring-4 focus:ring-emerald-600/10 disabled:cursor-not-allowed disabled:bg-slate-50';

function AuthShell({ eyebrow, title, description, children }) {
  return (
    <div className="min-h-screen bg-[#f7f4ef]">
      <div className="grid min-h-screen lg:grid-cols-[1.08fr_0.92fr]">
        <section className="relative hidden overflow-hidden bg-[radial-gradient(circle_at_top_left,_rgba(222,127,50,0.18),_transparent_28%),radial-gradient(circle_at_bottom_right,_rgba(21,107,74,0.22),_transparent_34%),linear-gradient(140deg,#15231d_0%,#11392c_44%,#1e5d46_100%)] lg:flex">
          <div className="absolute inset-0 opacity-[0.08]" style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,0.8) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.8) 1px, transparent 1px)', backgroundSize: '44px 44px' }} />
          <div className="absolute -left-24 top-16 h-72 w-72 rounded-full bg-[#de7f32]/20 blur-3xl" />
          <div className="absolute bottom-0 right-0 h-80 w-80 rounded-full bg-emerald-300/10 blur-3xl" />

          <div className="relative z-10 flex w-full flex-col justify-between p-12 xl:p-16">
            <div className="flex items-center gap-3">
              <div className="flex h-14 w-14 items-center justify-center rounded-[22px] border border-white/10 bg-white/10 text-white shadow-lg backdrop-blur">
                <img src="/logo.png" alt="Solaraith" className="h-8 w-auto" onError={(e) => { e.target.style.display = 'none'; }} />
              </div>
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-emerald-100">Solaraith</p>
                <p className="text-lg font-semibold text-white">Accounting Workspace</p>
              </div>
            </div>

            <div className="max-w-xl">
              <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-[#f4d8bd]">{eyebrow}</p>
              <h1 className="mt-4 text-5xl font-bold leading-[1.02] tracking-[-0.04em] text-white">
                Finance operations that feel calm, clear, and in control.
              </h1>
              <p className="mt-5 max-w-lg text-base leading-7 text-white">
                Move from billing to reporting without losing context. Solaraith keeps invoicing, expenses, customers, and project health in one focused workspace.
              </p>

              <div className="mt-10 grid gap-4">
                {brandFeatures.map((feature) => (
                  <div key={feature} className="flex items-center gap-3 rounded-[22px] border border-white/10 bg-white/8 px-4 py-4 backdrop-blur-sm">
                    <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-white/10 text-[#f6d5b8]">
                      <svg className="h-4.5 w-4.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <p className="text-sm font-medium text-white">{feature}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4 rounded-[28px] border border-white/10 bg-white/8 p-5 text-white/95 backdrop-blur-sm">
              <div>
                <p className="text-2xl font-bold text-white">24/7</p>
                <p className="mt-1 text-xs uppercase tracking-[0.18em] text-white/75">Visibility</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-white">Fast</p>
                <p className="mt-1 text-xs uppercase tracking-[0.18em] text-white/75">Collections</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-white">Secure</p>
                <p className="mt-1 text-xs uppercase tracking-[0.18em] text-white/75">Access</p>
              </div>
            </div>
          </div>
        </section>

        <section className="relative flex min-h-screen items-center justify-center px-5 py-8 sm:px-8 lg:px-12 xl:px-16">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(222,127,50,0.14),_transparent_18%),linear-gradient(180deg,#fcfbf8_0%,#f7f4ef_48%,#efe8df_100%)]" />

          <div className="relative z-10 w-full max-w-xl">
            <div className="mb-6 flex items-center gap-3 lg:hidden">
              <div className="flex h-12 w-12 items-center justify-center rounded-[20px] bg-emerald-700 text-white shadow-lg">
                <img src="/logo.png" alt="Solaraith" className="h-7 w-auto" onError={(e) => { e.target.style.display = 'none'; }} />
              </div>
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-emerald-700">Solaraith</p>
                <p className="text-base font-semibold text-slate-900">Accounting Workspace</p>
              </div>
            </div>

            <div className="rounded-[32px] border border-black/5 bg-white/86 p-6 shadow-[0_22px_54px_rgba(23,34,28,0.08)] backdrop-blur-xl sm:p-8">
              <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-emerald-700">{eyebrow}</p>
              <h2 className="mt-3 text-[2rem] font-bold tracking-[-0.04em] text-slate-950 sm:text-[2.2rem]">{title}</h2>
              <p className="mt-2 text-sm leading-6 text-slate-700">{description}</p>
              {children}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

function ErrorBanner({ message }) {
  if (!message) return null;
  return (
    <div className="mt-6 flex items-start gap-3 rounded-[22px] border border-rose-200 bg-rose-50 px-4 py-3.5 text-sm text-rose-700">
      <svg className="mt-0.5 h-4 w-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      <span>{message}</span>
    </div>
  );
}

function PrimaryBtn({ loading, children, disabled, ...rest }) {
  return (
    <button
      disabled={loading || disabled}
      className="flex w-full items-center justify-center gap-2 rounded-[20px] bg-gradient-to-r from-emerald-700 to-emerald-900 px-6 py-3.5 text-sm font-semibold text-white shadow-[0_18px_30px_rgba(21,107,74,0.22)] transition hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-60"
      {...rest}
    >
      {loading ? <><Spinner />{children}</> : children}
    </button>
  );
}

function Login({ onLogin }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [needsVerification, setNeedsVerification] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    let interval;
    if (resendTimer > 0) {
      interval = setInterval(() => setResendTimer((prev) => prev - 1), 1000);
    }
    return () => clearInterval(interval);
  }, [resendTimer]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const response = await authAPI.login(username, password);
      localStorage.setItem('token', response.data.token);
      onLogin(response.data.token);
      navigate('/');
    } catch (err) {
      if (err.response?.status === 403 && err.response?.data?.needsVerification) {
        setEmail(err.response.data.email);
        setNeedsVerification(true);
        setError('Your email needs to be verified. Check your inbox for the code.');
        setResendTimer(60);
      } else {
        setError(err.response?.data?.error || 'Invalid credentials. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifySubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    if (!verificationCode || verificationCode.length !== 6) {
      setError('Please enter the 6-digit verification code');
      setLoading(false);
      return;
    }
    try {
      const response = await authAPI.verify(email, verificationCode);
      localStorage.setItem('token', response.data.token);
      onLogin(response.data.token);
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.error || 'Verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setError('');
    setLoading(true);
    try {
      await authAPI.resendCode(email);
      setResendTimer(60);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to resend code');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      eyebrow={needsVerification ? 'Verify Access' : 'Welcome Back'}
      title={needsVerification ? 'Confirm your email to continue.' : 'Sign in to your finance workspace.'}
      description={needsVerification ? 'Use the verification code we sent to your inbox to complete sign-in securely.' : 'Pick up where you left off across billing, reporting, customers, and collections.'}
    >
      <ErrorBanner message={error} />

      {!needsVerification ? (
        <>
          <form onSubmit={handleSubmit} className="mt-8 space-y-5">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-slate-700">Username</label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter your username"
                required
                disabled={loading}
                className={authInputClass}
              />
            </div>

            <div>
              <div className="mb-1.5 flex items-center justify-between">
                <label className="block text-sm font-medium text-slate-700">Password</label>
                <Link to="/forgot-password" className="text-xs font-semibold text-emerald-700 transition hover:text-emerald-800 hover:underline">
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                  disabled={loading}
                  className={`${authInputClass} pr-12`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={loading}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 transition hover:text-slate-600"
                >
                  {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                </button>
              </div>
            </div>

            <PrimaryBtn type="submit" loading={loading}>Sign In</PrimaryBtn>
          </form>

          <p className="mt-7 text-center text-sm text-slate-500">
            Don&apos;t have an account?{' '}
            <Link to="/register" className="font-semibold text-emerald-700 transition hover:text-emerald-800 hover:underline">
              Create account
            </Link>
          </p>
        </>
      ) : (
        <>
          <div className="mt-8 rounded-[24px] border border-emerald-100 bg-emerald-50/70 p-4 text-sm text-slate-600">
            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-emerald-700">Verification Email</p>
            <p className="mt-2">
              We sent a 6-digit code to <span className="font-semibold text-slate-900">{email}</span>.
            </p>
          </div>

          <form onSubmit={handleVerifySubmit} className="mt-6 space-y-5">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-slate-700">Verification Code</label>
              <input
                type="text"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                maxLength={6}
                required
                disabled={loading}
                className="w-full rounded-[20px] border border-black/8 bg-white px-4 py-4 text-center font-mono text-2xl tracking-[0.45em] text-slate-900 outline-none transition focus:border-emerald-600 focus:ring-4 focus:ring-emerald-600/10 disabled:cursor-not-allowed disabled:bg-slate-50"
              />
            </div>

            <PrimaryBtn type="submit" loading={loading} disabled={verificationCode.length !== 6}>
              Verify &amp; Sign In
            </PrimaryBtn>
          </form>

          <p className="mt-6 text-center text-sm text-slate-500">
            Didn&apos;t receive the code?{' '}
            <button
              type="button"
              onClick={handleResendCode}
              disabled={resendTimer > 0 || loading}
              className="font-semibold text-emerald-700 transition hover:text-emerald-800 hover:underline disabled:cursor-not-allowed disabled:text-slate-400"
            >
              {resendTimer > 0 ? `Resend in ${resendTimer}s` : 'Resend Code'}
            </button>
          </p>
        </>
      )}
    </AuthShell>
  );
}

export default Login;
